/**
 * Primitivas monetarias.
 *
 * Todo monto en Tumin es un número entero de centavos (1/100 GTQ). Las tasas
 * son enteros en puntos básicos (1 pb = 0.01 %). El punto flotante solo
 * aparece de forma transitoria dentro de un cálculo y siempre se redondea de
 * vuelta a entero con `divideRounded` antes de salir de la función.
 */
/** Puntos básicos que hay en 100 %. */
export const BASIS_POINTS_PER_UNIT = 10_000;
/** Centavos que hay en un quetzal. */
export const CENTS_PER_QUETZAL = 100;
/** IVA (Impuesto al Valor Agregado) de Guatemala: 12 %. */
export const GUATEMALA_IVA_BP = 1200;
/** Cero centavos, ya tipado como `Money`. */
export const ZERO = 0;
/**
 * Épsilon que absorbe el error de representación binaria al redondear
 * (p. ej. 2.5 que llega como 2.4999999999). Es muy inferior a un centavo.
 */
const ROUNDING_EPSILON = 1e-9;
/** Umbral de redondeo "mitad hacia afuera": 0.5 sube al siguiente entero. */
const HALF = 0.5;
/** Lanza si el valor no es un entero seguro (fracción, NaN o fuera de rango). */
function assertSafeInteger(value, label) {
    if (!Number.isSafeInteger(value)) {
        throw new TypeError(`${label} requiere un entero seguro, se recibió: ${String(value)}`);
    }
}
/** Marca un entero como centavos. Lanza ante fracciones, NaN o enteros inseguros. */
export function cents(amount) {
    assertSafeInteger(amount, 'cents()');
    return amount;
}
/** Marca un entero como puntos básicos. Lanza ante fracciones, NaN o enteros inseguros. */
export function basisPoints(rate) {
    assertSafeInteger(rate, 'basisPoints()');
    return rate;
}
/**
 * División entera redondeada "mitad alejándose de cero", la regla de
 * redondeo de todo cálculo monetario (coincide con cómo redondean a mano los
 * cajeros).
 */
export function divideRounded(numerator, denominator) {
    if (denominator === 0)
        throw new RangeError('División entre cero');
    const quotient = Math.abs(numerator) / Math.abs(denominator);
    const sign = Math.sign(numerator) * Math.sign(denominator);
    return sign * Math.floor(quotient + HALF + ROUNDING_EPSILON) || 0;
}
/** Aplica una tasa en puntos básicos a un monto: `applyRate(Q100, 12%) = Q12`. */
export function applyRate(amount, rate) {
    return divideRounded(amount * rate, BASIS_POINTS_PER_UNIT);
}
/** Suma cualquier cantidad de montos. */
export function addMoney(...amounts) {
    return amounts.reduce((total, amount) => total + amount, 0);
}
/** Resta `subtrahend` de `minuend` (el resultado puede ser negativo). */
export function subtractMoney(minuend, subtrahend) {
    return (minuend - subtrahend);
}
/** El menor de dos montos. */
export function minMoney(a, b) {
    return Math.min(a, b);
}
/** El mayor de dos montos. */
export function maxMoney(a, b) {
    return Math.max(a, b);
}
/** Recorta resultados negativos a cero (p. ej. guardas contra cobros de más). */
export function nonNegative(amount) {
    return Math.max(0, amount);
}
/** Suma una propiedad numérica de una lista: `sumBy(rows, (r) => r.principal)`. */
export function sumBy(items, pick) {
    return items.reduce((total, item) => total + pick(item), 0);
}
/**
 * Divide un monto en `parts` porciones enteras cuya suma es exactamente
 * `amount`. El residuo va a la última porción para que las primeras queden
 * "redondas".
 */
export function splitEvenly(amount, parts) {
    if (!Number.isInteger(parts) || parts <= 0)
        throw new RangeError('parts debe ser > 0');
    const base = Math.floor(amount / parts);
    const slices = Array.from({ length: parts }, () => base);
    slices[parts - 1] = (amount - base * (parts - 1));
    return slices;
}
//# sourceMappingURL=money.js.map